import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {produce} from 'immer';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {Cell} from 'notebook/tables/TableConstants';
import {Schema} from 'schemas/SchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Clipboard from 'ui-library/Clipboard';
import * as Colors from 'ui-library/Colors';
import Modal from 'ui-library/modals/Modal';

import {makeTaskIdCell} from './MakeProcessCellUtils';

interface TaskFinderNotConfiguredErrorModalProps {
  processOutputSchema: Schema;
  onRequestClose: () => void;
}

export function TaskFinderNotConfiguredErrorModal(
  props: TaskFinderNotConfiguredErrorModalProps
): JSX.Element {
  return (
    <Modal
      title="Could not find associated tasks"
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Close',
          cancel: true,
        },
      ]}
    >
      <span>
        An output → task lookup has not been configured on the {props.processOutputSchema.name} schema.
      </span>
    </Modal>
  );
}

export enum TaskFinderErrorCode {
  NO_TASKS_FOUND = 'NO_TASKS_FOUND',
  MULTIPLE_TASKS_FOUND = 'MULTIPLE_TASKS_FOUND',
  LOOKUP_ERROR = 'LOOKUP_ERROR',
}

export interface TaskFinderError {
  rowId: string;
  rowIdx: number;
  errors: Array<string>;
  errorCode: TaskFinderErrorCode;
  tasks: Array<{id: string; humanId: string}> | null;
}

export interface TaskFinderErrorModalProps {
  taskIdColumnIdx: number;
  errors: Array<TaskFinderError>;
  currentCells: Array<Array<Cell>>;
  onRequestClose: () => void;
  onRequestSubmit: (updatedCells: Array<Array<Cell>>) => void;
}

export function ErrorTableRow(props: {error: TaskFinderError}): JSX.Element {
  return (
    <tr key={props.error.rowId}>
      <td className="text-right">{props.error.rowIdx + 1}</td>
      <td className="u-width-full">
        <div className={css(styles.errorText)}>
          <span>{props.error.errors.join(' ')}</span>
        </div>
      </td>
    </tr>
  );
}

function ErrorsTable(props: {errors: Array<TaskFinderError>}): JSX.Element {
  return (
    <table className="table table-striped selectable">
      <colgroup>
        <col className={css(styles.rowIndexCol)} />
        <col />
      </colgroup>
      <thead className={css(styles.errorTableHead)}>
        <tr>
          <th className={classNames('text-right', css(styles.errorTableHeaderCell))}>Row #</th>
          <th className={css(styles.errorTableHeaderCell)}>Message</th>
          {props.errors.length > 0 && (
            <th className={css(styles.errorTableHeaderCell)}>
              <Clipboard
                value={props.errors.map(({rowIdx, errors}) => `${rowIdx + 1} ${errors.join(' ')}`).join('\n')}
              >
                <a>Copy Errors</a>
              </Clipboard>
            </th>
          )}
        </tr>
      </thead>
      <tbody
        className={css(styles.errorTableBody)}
        {...getTestProps({component: 'TaskFinderErrorModal', element: 'tableBody'})}
      >
        {props.errors.map((error) => (
          <ErrorTableRow key={error.rowId} error={error} />
        ))}
      </tbody>
    </table>
  );
}

export default function TaskFinderErrorModal(props: TaskFinderErrorModalProps): JSX.Element {
  const {errors: unsortedErrors} = props;
  const errors = useMemo(() => [...unsortedErrors].sort((a, b) => a.rowIdx - b.rowIdx), [unsortedErrors]);
  return (
    <Modal
      title="Link issues"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        const updatedCells = produce(props.currentCells, (draftCells) => {
          let idxOffset = 0;
          errors.forEach((error: TaskFinderError) => {
            if (error.errorCode === TaskFinderErrorCode.MULTIPLE_TASKS_FOUND) {
              assert(
                error.tasks != null && error.tasks.length > 1,
                'Expected MULTIPLE_TASKS_FOUND error to include task ids'
              );
              const currentRow = props.currentCells[error.rowIdx];
              const modifyIdx = error.rowIdx + idxOffset;
              draftCells[modifyIdx][props.taskIdColumnIdx] = makeTaskIdCell({
                id: error.tasks[0].id,
                humanId: error.tasks[0].humanId,
              });
              error.tasks.slice(1).forEach(({id, humanId}) => {
                const newRow = produce(currentRow, (draftRow) => {
                  draftRow[props.taskIdColumnIdx] = makeTaskIdCell({id, humanId});
                });
                draftCells.splice(modifyIdx + 1, 0, newRow);
              });
              idxOffset += error.tasks.length - 1;
            }
          });
        });
        props.onRequestSubmit(updatedCells);
      }}
      buttons={[
        {
          text: 'Continue',
          submit: true,
        },
      ]}
    >
      <ErrorsTable errors={errors} />
    </Modal>
  );
}

const styles = StyleSheet.create({
  errorTableHeaderCell: {
    border: 'none',
  },
  errorTableHead: {
    borderBottom: `2px solid ${Colors.GRAY}`,
    display: 'block',
  },
  errorTableBody: {
    display: 'block',
    maxHeight: '400px',
    overflowY: 'scroll',
  },
  rowIndexCol: {
    width: '60px',
  },
  errorText: {
    whiteSpace: 'pre-wrap',
  },
});
